import { useMemo, useState } from 'react';
import { Button } from '@/ui/Button';
import { Card, CardBody, CardHeader } from '@/ui/Card';
import { Skeleton } from '@/ui/Skeleton';
import { EstadoError } from '@/components/EstadoError';
import { EstadoVacio } from '@/components/EstadoVacio';
import { Compositor } from './Compositor';
import { PanelDeCampanas, PanelDeValores } from './Gestion';
import { BarraDeValor, FilaDelRanking, Icono, TarjetaDeReconocimiento } from './piezas';
import {
  useAreas, useCampanas, useMuro, usePorValor, useRanking, useValores, type Periodo,
} from './api';

/**
 * Reconocimientos.tsx — la pantalla de `#kudos`.
 *
 * ## Quién ve qué
 *
 * El servidor exige `kudos:manage` para publicar, ni más ni menos. Aquí se
 * pregunta lo mismo, y esa comprobación gobierna media pantalla: quien solo
 * mira no ve los tres botones de arriba ni la tarjeta «Reconoce el buen
 * trabajo».
 *
 * El vanilla llevaba escrito por qué, y se conserva porque la corrección costó
 * un reporte: antes decía `Auth.can('kudos','manage') || Auth.isStaff()`, así
 * que cualquier rol de gestión abría el formulario, lo llenaba, y se lo
 * rechazaban con un 403 **al enviarlo**.
 *
 * ## El período es de la pantalla, no de cada tarjeta
 *
 * El ranking y «valores más reconocidos» miran el mismo período. En el vanilla
 * eso se conseguía leyendo el `<select>` del DOM desde las dos funciones y
 * llamando a `reloadLeaderboard()`, que refrescaba las dos a mano. Aquí es un
 * estado, y las dos consultas lo llevan en su clave: cambiar el período
 * refresca lo que depende de él y nada más.
 */

const PERIODOS: Array<[Periodo, string]> = [
  ['month', 'Este mes'],
  ['week', 'Esta semana'],
  ['all', 'Histórico'],
];

export function Reconocimientos() {
  const puedeReconocer = Auth.can('kudos', 'manage');

  const [periodo, setPeriodo] = useState<Periodo>('month');
  const [areaId, setAreaId] = useState('');
  const [compositor, setCompositor] = useState(false);
  const [panel, setPanel] = useState<'valores' | 'campanas' | null>(null);

  const valores = useValores();
  const muro = useMuro();
  const campanas = useCampanas(false);

  const porClave = useMemo(
    () => new Map((valores.data ?? []).map((v) => [v.key, v])),
    [valores.data],
  );

  return (
    <>
      <header className="page-header">
        <div>
          <h1 className="page-title">Reconocimientos</h1>
          <p className="page-subtitle">
            Reconocimientos publicados por Bienestar — a personas o a toda la empresa
          </p>
        </div>
        {puedeReconocer && (
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" size="sm" onClick={() => setPanel('valores')}>
              <i className="bi bi-award" aria-hidden="true" /> Valores
            </Button>
            <Button variant="secondary" size="sm" onClick={() => setPanel('campanas')}>
              <i className="bi bi-megaphone" aria-hidden="true" /> Campañas
            </Button>
            <Button onClick={() => setCompositor(true)}>
              <i className="bi bi-award-fill" aria-hidden="true" /> Dar reconocimiento
            </Button>
          </div>
        )}
      </header>

      {/* ── Campañas vigentes ────────────────────────────────────────── */}
      {!!campanas.data?.length && (
        <section className="mb-5" aria-label="Campañas vigentes">
          {campanas.data.map((c) => (
            <div
              key={c.id}
              className="mb-2 flex items-center gap-3 rounded-sm border px-4 py-3 last:mb-0"
              style={{
                background: `linear-gradient(135deg, ${c.color || '#F5A623'}22, transparent)`,
                borderColor: `${c.color || '#F5A623'}55`,
              }}
            >
              <div className="text-7 shrink-0">
                <Icono nombre={c.icon} respaldo="bi-bullseye" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-bold text-brand-ink">{c.title}</div>
                {c.description && <div className="text-2 text-muted">{c.description}</div>}
                {c.end_at && (
                  <div className="text-1 text-muted">
                    <i className="bi bi-calendar-event" aria-hidden="true" /> Hasta {c.end_at}
                  </div>
                )}
              </div>
              {puedeReconocer && (
                <Button size="sm" onClick={() => setCompositor(true)}>
                  <i className="bi bi-send" aria-hidden="true" /> Reconocer
                </Button>
              )}
            </div>
          ))}
        </section>
      )}

      {/* ── Invitación + ranking ─────────────────────────────────────── */}
      {puedeReconocer ? (
        <div className="mb-6 grid gap-5 md:grid-cols-2">
          <Card className="card-body text-center">
            <div className="text-9 text-muted">
              <i className="bi bi-award" aria-hidden="true" />
            </div>
            <div className="mt-1.5 text-4 font-extrabold text-brand-ink">
              Reconoce el buen trabajo
            </div>
            <p className="mt-1 text-3 text-muted">
              Publica un reconocimiento a una persona o a toda la empresa.
            </p>
            <Button className="mt-3 self-center" onClick={() => setCompositor(true)}>
              <i className="bi bi-award-fill" aria-hidden="true" /> Dar reconocimiento
            </Button>
          </Card>
          <TarjetaDeRanking {...{ periodo, setPeriodo, areaId, setAreaId }} />
        </div>
      ) : (
        <>
          {/* Para quien solo mira, la nota informativa no compite en ancho con
              el ranking, que sí es contenido. Queda en una línea encima. */}
          <p className="mb-5 flex items-center gap-2 rounded-sm bg-sunken px-3 py-2 text-2 text-muted">
            <i className="bi bi-info-circle" aria-hidden="true" />
            Bienestar publica los reconocimientos a personas y a toda la empresa para
            celebrar logros y valores.
          </p>
          <div className="mb-6">
            <TarjetaDeRanking {...{ periodo, setPeriodo, areaId, setAreaId }} />
          </div>
        </>
      )}

      {/* ── Valores más reconocidos ──────────────────────────────────── */}
      <Card className="mb-6">
        <CardHeader
          titulo={
            <>
              <i className="bi bi-bar-chart-fill text-brand-ink" aria-hidden="true" /> Valores más
              reconocidos
            </>
          }
        />
        <CardBody>
          <PorValor periodo={periodo} />
        </CardBody>
      </Card>

      {/* ── El muro ──────────────────────────────────────────────────── */}
      <h2 className="mb-4 text-5 font-bold text-brand-ink">Reconocimientos recientes</h2>
      {muro.isLoading && (
        <div className="grid grid-auto">
          {Array.from({ length: 6 }, (_, i) => <Skeleton key={i} className="h-40" />)}
        </div>
      )}
      {muro.isError && (
        <EstadoError err={muro.error} que="los reconocimientos" onReintentar={() => { muro.refetch(); }} />
      )}
      {!muro.isLoading && !muro.isError && !muro.data?.length && (
        <EstadoVacio
          icono="bi bi-award"
          titulo="Sin reconocimientos aún"
          descripcion={
            puedeReconocer
              ? 'Publica el primero y aparecerá aquí y en el muro de Novedades.'
              : 'Cuando Bienestar publique el primero, aparecerá aquí.'
          }
          accion={puedeReconocer ? { texto: 'Dar reconocimiento', onClick: () => setCompositor(true) } : undefined}
        />
      )}
      {!!muro.data?.length && (
        <div className="grid grid-auto">
          {muro.data.map((k, i) => (
            <TarjetaDeReconocimiento
              key={k.id ?? `${k.created_at}-${i}`}
              kudo={k}
              valor={porClave.get(k.badge)}
            />
          ))}
        </div>
      )}

      <Compositor
        abierto={compositor}
        alCerrar={() => setCompositor(false)}
        valores={valores.data ?? []}
      />
      <PanelDeValores abierto={panel === 'valores'} alCerrar={() => setPanel(null)} />
      <PanelDeCampanas abierto={panel === 'campanas'} alCerrar={() => setPanel(null)} />
    </>
  );
}

/* ── El ranking ────────────────────────────────────────────────────────── */

function TarjetaDeRanking({
  periodo, setPeriodo, areaId, setAreaId,
}: {
  periodo: Periodo;
  setPeriodo: (p: Periodo) => void;
  areaId: string;
  setAreaId: (a: string) => void;
}) {
  const { data, isLoading, isError, error, refetch } = useRanking(periodo, areaId);
  const areas = useAreas();

  return (
    <Card>
      <CardHeader
        titulo={<><i className="bi bi-trophy" aria-hidden="true" /> Ranking</>}
        // `<label>` con el texto en `sr-only`, que es el idioma del portal para
        // un filtro compacto (`novedades/Filtros.tsx`). El `<Select>` de
        // `ui/Field` no vale aquí: exige ir dentro de un `<Field>`, y `Field`
        // pinta su etiqueta encima — que en la cabecera de una tarjeta empujaría
        // el título a otra línea. La guarda tiene razón, así que se pone una
        // etiqueta de verdad en vez de saltársela.
        accion={
          <div className="flex gap-1.5">
            <label>
              <span className="sr-only">Período del ranking</span>
              <select
                className="rounded-sm border border-field-border bg-surface px-2 py-1 text-2 text-ink focus-visible:focus-anillo pointer-coarse:min-h-11"
                value={periodo}
                onChange={(e) => setPeriodo(e.target.value as Periodo)}
              >
                {PERIODOS.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
              </select>
            </label>
            <label>
              <span className="sr-only">Filtrar el ranking por área</span>
              <select
                className="rounded-sm border border-field-border bg-surface px-2 py-1 text-2 text-ink focus-visible:focus-anillo pointer-coarse:min-h-11"
                value={areaId}
                onChange={(e) => setAreaId(e.target.value)}
              >
                <option value="">Todas las áreas</option>
                {(areas.data ?? []).map((a) => (
                  <option key={a.id} value={String(a.id)}>{a.name}</option>
                ))}
              </select>
            </label>
          </div>
        }
      />
      <CardBody>
        {isLoading && (
          <div className="space-y-2">
            {Array.from({ length: 4 }, (_, i) => <Skeleton key={i} className="h-9" />)}
          </div>
        )}
        {isError && <EstadoError err={error} que="el ranking" onReintentar={() => { refetch(); }} />}
        {!isLoading && !isError && !data?.length && (
          <EstadoVacio icono="bi bi-trophy" titulo="Sin reconocimientos en este periodo" compacto />
        )}
        {data?.map((fila, i) => (
          <FilaDelRanking key={`${fila.full_name}-${i}`} fila={fila} puesto={i + 1} />
        ))}
      </CardBody>
    </Card>
  );
}

function PorValor({ periodo }: { periodo: Periodo }) {
  const { data, isLoading, isError, error, refetch } = usePorValor(periodo);
  const lista = data?.by_value ?? [];
  const maximo = Math.max(...lista.map((v) => v.count), 1);

  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 3 }, (_, i) => <Skeleton key={i} className="h-4" />)}
      </div>
    );
  }
  if (isError) {
    return <EstadoError err={error} que="el recuento por valor" onReintentar={() => { refetch(); }} />;
  }
  if (!lista.length) {
    return (
      <EstadoVacio icono="bi bi-bar-chart" titulo="Sin reconocimientos en este periodo" compacto />
    );
  }
  return (
    <>
      {lista.map((v, i) => (
        <BarraDeValor key={`${v.name}-${i}`} valor={v} maximo={maximo} />
      ))}
    </>
  );
}
